import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ChatMessage } from "@/api/types";
import { splitSources } from "@/api/client";
import { linkifyCitations } from "@/lib/citations";
import { cn } from "@/lib/cn";
import SourceList from "./SourceList";
import DocViewer from "./DocViewer";
import AnswerActions from "./AnswerActions";
import ConfirmCard from "./ConfirmCard";
import NoAnswerCard from "./NoAnswerCard";
import { AlertTriangle, Download, Eye, Loader2 } from "lucide-react";

// 대기 시간 표시: "오래 걸린다·지루하다" 민원 대응.
// 경과 시간이 길어질수록 안내 문구를 단계별로 바꿔 체감 대기시간을 줄인다.
const WAIT_STAGES: Array<{ after: number; msg: string }> = [
  { after: 60, msg: "거의 다 됐어요. 조금만 더 기다려 주세요…" },
  { after: 30, msg: "질문이 복잡해 시간이 조금 더 걸리고 있어요…" },
  { after: 10, msg: "관련 자료를 검토하고 있어요…" },
  { after: 0, msg: "답변 작성 중…" },
];

function fmtElapsed(s: number) {
  return s >= 60 ? `${Math.floor(s / 60)}분 ${s % 60}초` : `${s}초`;
}

// 스트리밍 본문 도착 전 대기 인디케이터.
// 진행률(progress) 이벤트가 도중에 와도 컴포넌트가 유지되어 타이머가 이어진다.
function WaitingIndicator({ progress }: { progress?: ChatMessage["progress"] }) {
  const startRef = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)),
      1000,
    );
    return () => clearInterval(t);
  }, []);
  // 빠른 답변에서 숫자가 깜빡이지 않도록 3초부터 카운터 노출
  const counter =
    elapsed >= 3 ? (
      <span className="shrink-0 text-xs tabular-nums opacity-70">{fmtElapsed(elapsed)}</span>
    ) : null;

  if (progress) {
    return (
      <div className="flex flex-col gap-1.5 text-muted-foreground">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          <span>{progress.message || "보고서 종합 중…"}</span>
          {counter}
        </div>
        {progress.total > 0 && (
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-[#0E1E2D] transition-all duration-300"
              style={{
                width: `${Math.min(100, Math.round((progress.done / progress.total) * 100))}%`,
              }}
            />
          </div>
        )}
      </div>
    );
  }

  const stageMsg = WAIT_STAGES.find((s) => elapsed >= s.after)!.msg;
  return (
    <div className="flex items-center gap-2 text-muted-foreground">
      <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
      <span>{stageMsg}</span>
      {counter}
    </div>
  );
}

interface Props {
  m: ChatMessage;
  prevQuestion: string;
  customer?: string;
  onConfirm: (botId: string, token: string, accept: boolean) => void;
  onFeedback: (id: string, fb: 1 | -1) => void;
  onRegenerate?: (question: string) => void;
  onRegenerateSources?: (question: string, excludeUrls: string[], customer?: string) => void;
  onSuggest?: (question: string) => void;
  onBookmark?: (id: string, bookmarkId: number | null) => void;
}

export default function BotBubble({
  m,
  prevQuestion,
  customer,
  onConfirm,
  onFeedback,
  onRegenerate,
  onRegenerateSources,
  onSuggest,
  onBookmark,
}: Props) {
  const [activeCite, setActiveCite] = useState<number | null>(null);
  // 산출물 인라인 뷰어(모달) 대상 — null 이면 닫힘
  const [doc, setDoc] = useState<{ name: string; url: string; downloadUrl: string } | null>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const split = splitSources(m.text);
  const sources = (m.sources?.length ?? 0) > 0 ? m.sources! : split.sources;
  const sourceMeta = m.sourceMeta ?? [];
  // 각주 유효 범위: sourceMeta 있으면 그 길이, 없으면 sources 길이
  const maxIndex = sourceMeta.length || sources.length;

  const registerItemRef = useCallback((index: number, el: HTMLDivElement | null) => {
    if (el) itemRefs.current.set(index, el);
    else itemRefs.current.delete(index);
  }, []);

  const onCite = useCallback((n: number) => {
    setActiveCite(n);
    const el = itemRefs.current.get(n);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  // [n] 각주를 클릭 칩으로 치환하는 마크다운 렌더러
  const components = useMemo<Components>(() => {
    const opts = { maxIndex, active: activeCite, onCite };
    const wrap =
      (Tag: "p" | "li" | "td" | "th") =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ({ node: _node, children, ...props }: any) => (
        <Tag {...props}>{linkifyCitations(children, opts)}</Tag>
      );
    return { p: wrap("p"), li: wrap("li"), td: wrap("td"), th: wrap("th") };
  }, [maxIndex, activeCite, onCite]);

  return (
    <div className="flex justify-start">
      <div className="max-w-[92%] rounded-2xl rounded-bl-sm border border-border bg-card px-3.5 py-2.5 text-sm text-card-foreground">
        {m.blocked ? (
          <div className="flex items-start gap-2 text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{m.text || "민감정보가 포함되어 답변을 제공할 수 없습니다."}</span>
          </div>
        ) : m.streaming && !m.text ? (
          <WaitingIndicator progress={m.progress} />
        ) : m.noAnswer ? (
          <NoAnswerCard escalation={m.escalation} question={prevQuestion} />
        ) : (
          <div className={cn("md", m.streaming && "cursor-blink", m.error && "text-destructive")}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={m.streaming ? undefined : components}>
              {split.body}
            </ReactMarkdown>
          </div>
        )}

        {!m.streaming && !m.blocked && !m.noAnswer && (
          <SourceList
            sources={sources}
            sourceMeta={sourceMeta}
            activeIndex={activeCite}
            registerItemRef={registerItemRef}
            question={prevQuestion || undefined}
            customer={customer}
            onRegenerate={onRegenerateSources}
          />
        )}

        {!m.streaming && (m.downloads?.length ?? 0) > 0 && (
          <div className="mt-3 space-y-1.5 border-t border-border pt-2">
            <div className="text-xs font-semibold text-muted-foreground">산출물</div>
            {m.downloads!.map((d, k) => (
              <div
                key={k}
                className="flex items-center gap-1 rounded-md bg-[#0E1E2D] px-1.5 py-1 text-xs font-semibold text-white"
              >
                {/* 파일명 클릭 = 바로 보기(미리보기 가능한 형식일 때). 아니면 다운로드 */}
                {d.preview_url ? (
                  <button
                    onClick={() => setDoc({ name: d.name, url: d.preview_url!, downloadUrl: d.url })}
                    className="flex min-w-0 flex-1 items-center gap-1.5 rounded px-1.5 py-0.5 text-left hover:bg-white/15"
                    title="문서 바로 보기"
                  >
                    <Eye className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{d.name}</span>
                  </button>
                ) : (
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex min-w-0 flex-1 items-center gap-1.5 rounded px-1.5 py-0.5 hover:bg-white/15"
                  >
                    <span className="truncate">{d.name}</span>
                  </a>
                )}
                <a
                  href={d.url}
                  download
                  className="shrink-0 rounded p-1 hover:bg-white/15"
                  aria-label={`${d.name} 다운로드`}
                  title="다운로드"
                >
                  <Download className="h-3.5 w-3.5" />
                </a>
              </div>
            ))}
          </div>
        )}

        {m.externalRequired && m.confirmToken && (
          <ConfirmCard
            onConfirm={() => onConfirm(m.id, m.confirmToken!, true)}
            onDecline={() => onConfirm(m.id, m.confirmToken!, false)}
          />
        )}

        {!m.streaming && !m.blocked && !m.error && !m.externalRequired && (
          <AnswerActions
            msg={m}
            question={prevQuestion}
            customer={customer}
            onFeedback={(fb) => onFeedback(m.id, fb)}
            onRegenerate={onRegenerate && prevQuestion ? () => onRegenerate(prevQuestion) : undefined}
            onBookmark={onBookmark ? (bid) => onBookmark(m.id, bid) : undefined}
          />
        )}

        {!m.streaming && onSuggest && (m.suggestions?.length ?? 0) > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5 border-t border-border pt-2">
            {m.suggestions!.map((s, k) => (
              <button
                key={k}
                onClick={() => onSuggest(s)}
                className="rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground hover:bg-muted"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {doc && (
        <DocViewer
          name={doc.name}
          url={doc.url}
          downloadUrl={doc.downloadUrl}
          onClose={() => setDoc(null)}
        />
      )}
    </div>
  );
}
